import { useId, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  STAGE_LABEL,
  casePath,
  isArchivedCase,
  redoOriginalInSystem,
  type DueCountdown,
  type JobOrder,
  type Kit,
  caseFinished,
  dueCountdown,
  kitStatus,
} from '@/domain';
import {
  useHoldOrder,
  useJobOrder,
  useReleaseHold,
  useSetOrderDates,
} from '@/application/usecases/useOrders';
import { useKitPhotos, useMarkUrgent } from '@/application/usecases/useKits';
import { useSession } from '@/application/usecases/useSession';
import { formatDay, labToday } from '@/shared/format/instant';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import { CasePhotos } from '@/features/queue/CasePhotos';
import { DeliveryDate } from '@/ui/data/DeliveryDate';
import { StatusDot } from '@/ui/data/StatusDot';
import { PrintIcon, RedoIcon } from '@/ui/icons';
import { drawerStyles } from '@/ui/layout/Drawer';
import { useConfirmAction } from '@/features/shell/useConfirmAction';
import { SkeletonCase } from '@/ui/feedback/Loading';
import { AssignDialog } from '@/features/queue/AssignDialog';
import { ClientLink } from '@/features/clients/ClientLink';
import { DeletionControls } from './DeletionControls';
import { DeliverTo } from './DeliverTo';
import { HoldDialog } from './HoldDialog';
import styles from './OrderCaseView.module.css';

/** Where Back on a screen this view leads to returns: the page the case was opened on. */
export interface CaseBackTo {
  path: string;
  label: string;
}

export interface OrderCaseViewProps {
  /** The case, by its job order number. Nothing is asked for, and nothing drawn, without one. */
  jobOrderNo: string | undefined;
  /**
   * The case has gone: deleted, or its deletion approved. Whatever holds the
   * view closes, since the case is no longer on the list it was opened from.
   */
  onGone: () => void;
  /**
   * Where the full record's Back returns to, for a view opened somewhere other
   * than Orders. Left off on Orders, whose record already goes back to it.
   */
  backTo?: CaseBackTo;
}

/**
 * A case in hand, with everything that can be done to it: who it is for, what
 * it remakes, whether it is stopped and why, its trays with their photographs,
 * where it is delivered, its dates, and deleting it.
 *
 * This is the content of the Orders drawer, and it was private to that screen
 * until the request of 6 October 2026 (fifth), section 3: a case opened from a
 * doctor's or a patient's page now opens there, in place, with "the same
 * shared case view used elsewhere, with all actions". So the view is a piece
 * of its own. It wraps no drawer: Orders puts it in its drawer, and a case
 * opened in place puts it in a drawer or, on a phone, in a page over the
 * screen. It does not assume it is on Orders either: every way out of it is
 * asked of `casePath`, by the screens the reader holds.
 *
 * The dialogs it opens (assign, hold, delete) are sheets drawn in the body by
 * their own portal, so they are outside whatever holds the view: a fixed
 * dialog inside a sliding panel would slide with it.
 */
export function OrderCaseView({ jobOrderNo, onGone, backTo }: OrderCaseViewProps) {
  const navigate = useNavigate();
  const { data } = useJobOrder(jobOrderNo);
  const kits = data?.kits ?? [];
  const session = useSession();
  const views = session.data?.views ?? [];
  const markUrgent = useMarkUrgent();
  // Mark urgent is asked about first. The request of 5 October 2026 (third), section 2.
  const ask = useConfirmAction();

  /*
   * The two manager actions the client could not find. Both endpoints and
   * both permissions existed from the start; nothing on a screen called them.
   */
  const canMarkUrgent = session.data?.permissions.includes('markUrgent') === true;
  const canEditDates = session.data?.permissions.includes('editDates') === true;
  // The edit screen is a screen of Orders, so it is offered to whoever holds both.
  const canEditCase =
    session.data?.permissions.includes('editCase') === true && views.includes('orders');
  // Assign, beside Mark urgent on each kit: the person, where Flag is the department.
  const canAssign = session.data?.permissions.includes('assign') === true;
  const [assigning, setAssigning] = useState<Kit | null>(null);

  /*
   * Hold and Release, on a case wherever it stands. The request of 6 October
   * 2026 (second), item 3: "the hold should work for cases that are in
   * production too". The server always allowed it; the buttons were on
   * Payments and Shipping only, and neither lists a case still being made.
   *
   * The two permissions that put a hold on are the two that lift it, and the
   * server decides either way. Hold opens the dialog Payments and Shipping
   * open, which is its own confirmation; Release is asked about first, in the
   * sentence the other screens ask it in. Somebody with neither permission
   * still reads that the case is stopped, and why.
   */
  const hold = useHoldOrder();
  const release = useReleaseHold();
  const mayHold =
    session.data?.permissions.includes('pay') === true ||
    session.data?.permissions.includes('ship') === true;
  // The case the Hold dialog is open for, or null while it is closed.
  const [holding, setHolding] = useState<string | null>(null);

  if (jobOrderNo === undefined) return null;
  // The shape of the order while it loads, so the panel never slides in empty.
  if (data === undefined) return <SkeletonCase label={strings.common.loadingCase} />;

  // The record's own screen, by the screens held: on Orders for whoever holds it.
  const recordPath = casePath(views, { jobOrderNo: data.jobOrder.jobOrderNo }, 'record');
  // Said only when there is somewhere to go back to that the record would not find itself.
  const leaving = backTo === undefined ? undefined : { state: { backTo } };
  // How the date stands, in words; nothing for a finished case.
  const countdown = countdownWords(
    dueCountdown(
      data.jobOrder.deliveryDate,
      labToday(),
      data.jobOrder.daysLeft,
      caseFinished(data.jobOrder),
    ),
  );

  return (
    <>
      <div className={drawerStyles.lead}>
        {/*
          The doctor's name opens the doctor's profile, for a reader who
          holds that screen; for anybody else it is the plain name, and
          Hidden where the name is withheld. The request of 6 October 2026
          (second), item 4.
        */}
        <span className={styles.leadName}>
          <ClientLink kind="doctor" id={data.jobOrder.doctorId} name={data.jobOrder.dentistName} />
        </span>{' '}
        · {kits.length} {kits.length === 1 ? strings.orders.kit : strings.orders.kitsPlural} ·{' '}
        {/* "overdue by 3 days", "due today", "due in 5 days": never a negative count. */}
        {countdown !== null && (
          <>
            <span data-testid="case-countdown">{countdown}</span> ·{' '}
          </>
        )}
        {/* The date itself, in its tone. The view is a case's own screen,
            so this is one of the three places the date pulses. Section 4. */}
        <DeliveryDate
          date={data.jobOrder.deliveryDate}
          finished={caseFinished(data.jobOrder)}
          emphasis
        />
      </div>

      <CaseRedo order={data.jobOrder} />

      {/* The number on the doctor's paper. The lab's request of 17 October 2026, item 7. */}
      {data.jobOrder.serialNumber != null && (
        <p className={styles.redo}>
          {strings.serialNumber.short} <b>{data.jobOrder.serialNumber}</b>
        </p>
      )}

      {/*
        Stopped, since when and why, before anything else about the case:
        nobody may work it until the hold is lifted, so it is the first
        thing to know. Read by everybody; Release for whoever may lift it.
      */}
      {data.jobOrder.hold.held && (
        <div className={styles.hold} role="group" aria-label={strings.payments.onHold}>
          <StatusDot tone="hold">
            {data.jobOrder.hold.heldAt === null
              ? strings.payments.onHold
              : `${strings.onHold.since} ${formatDay(data.jobOrder.hold.heldAt)}`}
          </StatusDot>
          {data.jobOrder.hold.reason !== null && (
            <p className={styles.holdReason}>{data.jobOrder.hold.reason}</p>
          )}
          {mayHold && (
            <Button
              disabled={release.isPending}
              onClick={() =>
                ask(
                  strings.confirm.releaseHold(data.jobOrder.jobOrderNo),
                  () =>
                    release.mutateAsync(data.jobOrder.jobOrderNo).then(() => {
                      toast(`${data.jobOrder.jobOrderNo} ${strings.payments.holdReleased}`);
                    }),
                  { confirmLabel: strings.payments.releaseHold },
                )
              }
            >
              {strings.payments.releaseHold}
            </Button>
          )}
        </div>
      )}

      {/*
        The whole record, live. The view is a summary; a manager chasing
        a case wants the timeline, the photographs and every note, which
        is the archive's screen, and the archive's endpoint already serves
        a live order.
      */}
      <div className={`${drawerStyles.tools} ${styles.tools}`}>
        {recordPath !== null && (
          <Button onClick={() => void navigate(recordPath, leaving)}>
            {strings.orders.fullRecord}
          </Button>
        )}
        {/* Behind editCase, which the matrix seeds to Management. Handover 2.4. */}
        {canEditCase && (
          <Button onClick={() => void navigate(`/orders/${data.jobOrder.jobOrderNo}/edit`)}>
            {strings.orders.editCase}
          </Button>
        )}
        {mayHold && mayBeHeld(data.jobOrder) && (
          <Button onClick={() => setHolding(data.jobOrder.jobOrderNo)}>
            {strings.payments.hold}
          </Button>
        )}
      </div>

      {/*
        The card opens the DIGITAL case. It used to go straight to the
        printable sheet, which put a piece of paper in front of somebody who
        wanted to read the case. Printing is an action on the case now, not
        the way into it.
      */}
      {kits.map((kit) => (
        <div key={kit.id} className={drawerStyles.card}>
          <button
            className={drawerStyles.cardOpen}
            aria-label={`${strings.orders.openCase} ${kit.id}`}
            onClick={() => {
              // Reception holds no bench, so the tray opens as its order.
              const path = casePath(views, { jobOrderNo: kit.jobOrderNo, kitId: kit.id }, 'kit');
              if (path !== null) void navigate(path);
            }}
          >
            <span className={drawerStyles.cardTop}>
              <span className={drawerStyles.cardId}>{kit.id}</span>
              <StatusDot
                tone={
                  // Rework first, then the due date by the one rule. The request of 7 October 2026, section 6.
                  kitStatus(
                    {
                      urgent: false,
                      rework: kit.rework,
                      deliveryDate: kit.deliveryDate,
                      finished: kit.stage === 'ready',
                    },
                    labToday(),
                  ).tone
                }
              >
                {STAGE_LABEL[kit.stage]}
              </StatusDot>
            </span>
            <span className={drawerStyles.cardMeta}>
              {kit.product} · {kit.units}{' '}
              {kit.units === 1 ? strings.job.toothUnit : strings.job.toothUnits}
              {kit.teeth.length > 0 && ` · ${kit.teeth.join(', ')}`} · {strings.orders.shade}{' '}
              {kit.shade}
            </span>
          </button>

          <button className={drawerStyles.cardPrint} onClick={() => void navigate(`/print/${kit.id}`)}>
            <PrintIcon /> {strings.orders.printSheet}
          </button>

          {canMarkUrgent && (
            <button
              className={drawerStyles.cardPrint}
              disabled={markUrgent.isPending}
              onClick={() =>
                ask(
                  // By the tray when the order has several: urgent is set on one of them.
                  strings.confirm.urgent(kits.length === 1 ? kit.jobOrderNo : kit.id, !kit.urgent),
                  () => markUrgent.mutateAsync({ kitId: kit.id, urgent: !kit.urgent }),
                  { confirmLabel: kit.urgent ? strings.orders.clearUrgent : strings.orders.markUrgent },
                )
              }
            >
              {kit.urgent ? strings.orders.clearUrgent : strings.orders.markUrgent}
            </button>
          )}

          {canAssign && (
            <button className={drawerStyles.cardPrint} onClick={() => setAssigning(kit)}>
              {strings.assign.action}
              {kit.assignedToUserName !== null && ` · ${kit.assignedToUserName}`}
            </button>
          )}

          {/* What the case looked like when it came in, under its card.
              Section 6 of the request of 16 September 2026. */}
          {kit.photoCount > 0 && <KitPhotos kitId={kit.id} jobOrderNo={kit.jobOrderNo} />}
        </div>
      ))}

      {/*
        Where the finished case goes: the doctor's clinic, address and number,
        for a reader who may see identity. Nothing for anybody else, or for a
        case with no doctor's record behind it. The request of 6 October 2026
        (fifth), section 2.
      */}
      <DeliverTo deliverTo={data.jobOrder.deliverTo} className={styles.deliverTo} />

      {canEditDates && kits[0] !== undefined && (
        <DatesForm
          jobOrderNo={data.jobOrder.jobOrderNo}
          rowVersion={data.jobOrder.rowVersion}
          startDate={kits[0].startDate}
          deliveryDate={data.jobOrder.deliveryDate}
        />
      )}

      {/*
        Deleting the case, or the deletion waiting on it. A case that has
        gone closes what holds the view, since it is no longer on the list.
      */}
      <DeletionControls order={data.jobOrder} onDeleted={onGone} />

      {/* Sheets, drawn in the body: outside the panel this view is in, whichever it is. */}
      {assigning !== null && <AssignDialog kit={assigning} onClose={() => setAssigning(null)} />}
      {holding !== null && (
        <HoldDialog
          jobOrderNo={holding}
          busy={hold.isPending}
          onCancel={() => setHolding(null)}
          onConfirm={(reason) =>
            hold.mutate(
              { jobOrderNo: holding, reason },
              {
                onSuccess: () => {
                  toast(`${holding} ${strings.payments.putOnHold}`);
                  setHolding(null);
                },
                onError: (error: Error) => toast(failureWords(error)),
              },
            )
          }
        />
      )}
    </>
  );
}

/**
 * The case photographs under a kit card. Its own component so the query is
 * only made for a kit that has any: the view draws a card per tray, and a hook
 * cannot be called conditionally inside a loop.
 */
function KitPhotos({ kitId, jobOrderNo }: { kitId: string; jobOrderNo: string }) {
  const photos = useKitPhotos(kitId);
  if ((photos.data?.length ?? 0) === 0) return null;
  return <CasePhotos photos={photos.data ?? []} jobOrderNo={jobOrderNo} />;
}

/**
 * Whether Hold is offered on a case: one that is not stopped already and is
 * still the lab's to stop. A dispatched case has left, a deleted one is gone,
 * and an archived one is off every list of work in hand. The server decides
 * either way; this only keeps a button that would be refused off the screen.
 */
function mayBeHeld(order: JobOrder): boolean {
  return (
    !order.hold.held &&
    !order.dispatch.dispatched &&
    order.deletion.status !== 'deleted' &&
    !isArchivedCase(order)
  );
}

/**
 * What a remake remakes, under the lead, with the mark its row carries on the
 * list. The request of 6 October 2026 (second), items 1 and 2.
 *
 * An original this system holds opens its record. One from before the system
 * is a number somebody typed and no case here, so it is plain text and says
 * so: `redoOriginalInSystem` is the rule, and it is never a link.
 */
function CaseRedo({ order }: { order: JobOrder }) {
  const session = useSession();
  if (order.redoOf === null) return null;

  const path = redoOriginalInSystem(order)
    ? casePath(session.data?.views ?? [], { jobOrderNo: order.redoOf }, 'record')
    : null;

  return (
    <p className={styles.redo}>
      <span className={styles.redoMark}>
        <RedoIcon size={13} />
      </span>
      {redoOriginalInSystem(order) ? (
        <span>
          {strings.redo.redoOf}{' '}
          {path === null ? order.redoOf : <Link to={path}>{order.redoOf}</Link>}
        </span>
      ) : (
        <span>{strings.redo.original(order.redoOf, true)}</span>
      )}
      {order.redoReason !== null && <span className={styles.redoReason}>{order.redoReason}</span>}
    </p>
  );
}

/**
 * Start and delivery dates, for a holder of `editDates`.
 *
 * Both travel together and the rowVersion goes with them, so two managers
 * editing the same order get a 409 they can read rather than one silently
 * overwriting the other. Local state until Save: a date field that saved on
 * every keystroke would send a half typed year to the server.
 *
 * Save dates asks first, with both days in the question, and Cancel leaves the
 * two fields as they were typed. The request of 5 October 2026 (third),
 * section 2.
 */
function DatesForm({
  jobOrderNo,
  rowVersion,
  startDate,
  deliveryDate,
}: {
  jobOrderNo: string;
  rowVersion: string;
  startDate: string;
  deliveryDate: string;
}) {
  const setDates = useSetOrderDates();
  const ask = useConfirmAction();
  const [start, setStart] = useState(startDate);
  const [delivery, setDelivery] = useState(deliveryDate);
  const ids = { start: useId(), delivery: useId() };
  const dirty = start !== startDate || delivery !== deliveryDate;

  return (
    <div className={drawerStyles.dates}>
      <div className={drawerStyles.datesTitle}>{strings.orders.editDates}</div>
      <div className={drawerStyles.dateRow}>
        <label className={drawerStyles.dateField}>
          <span>{strings.orders.startDate}</span>
          <input
            id={ids.start}
            type="date"
            value={start}
            onChange={(event) => setStart(event.target.value)}
          />
        </label>
        <label className={drawerStyles.dateField}>
          <span>{strings.orders.deliveryDate}</span>
          <input
            id={ids.delivery}
            type="date"
            value={delivery}
            onChange={(event) => setDelivery(event.target.value)}
          />
        </label>
      </div>
      <Button
        variant="primary"
        disabled={!dirty || setDates.isPending}
        onClick={() =>
          ask(
            strings.confirm.saveDates(jobOrderNo, start, delivery),
            () =>
              setDates
                .mutateAsync({ jobOrderNo, startDate: start, deliveryDate: delivery, rowVersion })
                .then(() => {
                  toast(`${jobOrderNo} ${strings.orders.datesSaved}`);
                }),
            { confirmLabel: strings.orders.saveDates },
          )
        }
      >
        {strings.orders.saveDates}
      </Button>
    </div>
  );
}

/** How a case's date stands, in the words of the line under its number; nothing for a finished case. */
function countdownWords(countdown: DueCountdown | null): string | null {
  if (countdown === null) return null;
  if (countdown.kind === 'overdue') return strings.orders.countdownOverdue(countdown.days);
  if (countdown.kind === 'today') return strings.orders.countdownToday;
  return strings.orders.countdownIn(countdown.days);
}
